/**
 * The single confirmation dialog — Document 6 §8.3.
 *
 * Used only where the action actually destroys something. Reversible actions
 * get an undo toast instead: a prompt that appears on harmless actions trains
 * people to dismiss prompts without reading them, which is exactly what must
 * not happen on the one that deletes a student's photographs.
 */

import { useEffect, useId, useRef } from 'react';
import { Button } from './Button.js';
import './ConfirmDialog.css';

interface ConfirmDialogProps {
  title: string;
  body: string;
  confirmLabel: string;
  /** Marks the action as destructive so the button reads as one. */
  destructive?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export function ConfirmDialog({
  title,
  body,
  confirmLabel,
  destructive = false,
  onCancel,
  onConfirm,
}: ConfirmDialogProps) {
  const titleId = useId();
  const cancelRef = useRef<HTMLButtonElement>(null);

  // Doc 6 §19: focus starts on cancel, never on the action that proceeds.
  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onCancel]);

  return (
    <div className="confirm__backdrop" role="presentation">
      <div
        className="confirm__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <h2 className="confirm__title" id={titleId}>
          {title}
        </h2>
        <p className="confirm__body">{body}</p>

        <div className="confirm__actions">
          <Button type="button" variant="secondary" onClick={onCancel} ref={cancelRef}>
            Vazgeç
          </Button>
          <Button
            type="button"
            variant={destructive ? 'danger' : 'primary'}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
